import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Delete as DeleteIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { ILegalTransactionFullObject } from '../../Interfaces/ILegalTransaction';
import {
  ILegalTransactionRelationship,
  initLegalTransactionRelationship,
} from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { AssignLegalClientsDialog } from '../LegalClient/AssignLegalClientsDialog';
import PolicyIcon from '@mui/icons-material/Policy';
import { RegisterDocumentDownloadDialog } from '../../ProcessManager/CommercialRegister/RegisterDocumentDownloadDialog';

interface IProps {
  legalTransaction: ILegalTransactionFullObject;
  relationsships: ILegalTransactionRelationship[];
  setRelationships: Function;
  /**
   * Externer Trigger zum Oeffnen des Assign-Dialogs. Parent (ProcessManagerTab)
   * inkrementiert das Token, wenn auf der Karte +-Button -> "Firma" geklickt
   * wird.
   */
  addTriggerToken?: number;
  /** Plus-Button INNERHALB der Tabelle anzeigen (default true). */
  showInlineAddButton?: boolean;
}

type CompanyRow = {
  key: string;
  idRelationship: number;
  idLegalClient: number | null;
  companyName: string | null;
  businessForm: string | null;
  registerCourt: string | null;
  registerNumber: string | null;
  source: 'Datenerfassung' | 'Mandanten';
};

const nextTempRelId = (rels: ILegalTransactionRelationship[]) => {
  const minId = rels.reduce((m, r) => Math.min(m, r.idRelationship), 0);
  return minId <= 0 ? minId - 1 : -1;
};

function addCompanyRelationshipsDirect(
  clients: ILegalClient[],
  relationsships: ILegalTransactionRelationship[],
  setRelationships: Function,
  legalTransactionId?: number
) {
  const existingClientIds = new Set(
    relationsships
      .filter(r => r.idRelationshipType === 4)
      .flatMap(r => r.Members ?? [])
      .map(m => m.idLegalClient)
  );

  const additions: ILegalTransactionRelationship[] = [];
  let relId = nextTempRelId(relationsships);

  for (const legalClient of clients) {
    if (!legalClient || existingClientIds.has(legalClient.idLegalClient))
      continue;

    const base = initLegalTransactionRelationship();

    const rel: ILegalTransactionRelationship = {
      ...base,
      idRelationship: relId,
      idRelationshipType: 4,
      RelationshipType: 'Firma',
      idLegalTransaction: legalTransactionId ?? base.idLegalTransaction,
      data: {},
      Members: [
        {
          FirstName: legalClient.FirstName ?? null,
          LastName: legalClient.LastName ?? legalClient.SurName ?? '',
          LegalClient: legalClient,
          LegalTransactionArray: [],
          RelationshipRole: 'Firma',
          idLegalClient: legalClient.idLegalClient,
          idLegalClientRelationship: relId - 100000,
          idRelationshipRole: 11,
          isActiveLegalClientRelationship: true,
        } as any,
      ],
    };

    additions.push(rel);
    existingClientIds.add(legalClient.idLegalClient);
    relId -= 1;
  }

  if (additions.length)
    (setRelationships as any)([...relationsships, ...additions]);
}

export const LegalTransactionCompany: React.FC<IProps> = ({
  legalTransaction,
  relationsships,
  setRelationships,
  addTriggerToken,
  showInlineAddButton = true,
}) => {
  const [openAssign, setOpenAssign] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  // Externer Trigger (Karten-Header +-Button) -> Assign-Dialog oeffnen
  const lastTriggerRef = useRef<number | undefined>(addTriggerToken);
  useEffect(() => {
    if (
      addTriggerToken !== undefined &&
      addTriggerToken !== lastTriggerRef.current
    ) {
      lastTriggerRef.current = addTriggerToken;
      setOpenAssign(true);
    }
  }, [addTriggerToken]);

  const takenLegalClients: ILegalClient[] = useMemo(
    () =>
      (Array.isArray(relationsships) ? relationsships : [])
        .filter(r => r.idRelationshipType === 4)
        .flatMap(r => r.Members ?? [])
        .map(m => m.LegalClient)
        .filter(Boolean) as ILegalClient[],
    [relationsships]
  );

  const rows: CompanyRow[] = useMemo(() => {
    const relsFirma = (
      Array.isArray(relationsships) ? relationsships : []
    ).filter(r => r.idRelationshipType === 4);
    return relsFirma.flatMap((rel, idxRel) => {
      const members = rel.Members ?? [];
      if (members.length === 0) return [];
      return members.map((m, idxMem) => {
        const legalClient: any = m.LegalClient;
        const companyName =
          legalClient?.LastName ?? legalClient?.SurName ?? null;
        const businessForm = legalClient?.BusinessForm ?? null;
        const registerCourt = legalClient?.Court ?? null;
        const registerNumber = legalClient?.RegisterNumber ?? null;
        const source: 'Datenerfassung' | 'Mandanten' =
          legalClient?.idLegalClientType === 2 ? 'Mandanten' : 'Datenerfassung';
        return {
          key: `rel-${rel.idRelationship}-${idxRel}-m-${m.idLegalClient ?? idxMem}`,
          idRelationship: rel.idRelationship,
          idLegalClient: m.idLegalClient ?? null,
          companyName,
          businessForm,
          registerCourt,
          registerNumber,
          source,
        };
      });
    });
  }, [relationsships]);

  if (!Array.isArray(relationsships)) {
    return (
      <Alert severity='error'>
        Unerwartetes Format: Keine Unternehmensdaten gefunden.
      </Alert>
    );
  }

  const handleAssignClients = (clients: ILegalClient[]) => {
    addCompanyRelationshipsDirect(
      clients,
      relationsships,
      setRelationships,
      legalTransaction?.idLegalTransaction
    );
    setOpenAssign(false);
  };

  const handleRemove = (idRelationship: number) => {
    setRelationships((prev: any[]) =>
      prev.map(rel =>
        rel.idRelationship === idRelationship
          ? { ...rel, isActiveRelationship: 0 }
          : rel
      )
    );
  };

  return (
    <>
      {selectedClientId !== null && (
        <RegisterDocumentDownloadDialog
          idLegalClient={selectedClientId}
          open={openDialog}
          onClose={() => setOpenDialog(false)}
        />
      )}

      {showInlineAddButton && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box sx={{ flex: 1 }} />
          <Tooltip title='Unternehmen / juristische Person hinzufügen'>
            <IconButton onClick={() => setOpenAssign(true)} size='small'>
              <Add />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Unternehmen</TableCell>
            <TableCell width={160}>Registergericht</TableCell>
            <TableCell width={150}>Registernummer</TableCell>
            <TableCell width={140} align='right'>
              Aktionen
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>
                <i>Es liegen keine Unternehmensdaten vor.</i>
              </TableCell>
            </TableRow>
          ) : (
            rows.map(x => (
              <TableRow key={x.key} hover>
                <TableCell>{x.companyName ?? '—'}</TableCell>
                <TableCell>{x.registerCourt ?? '—'}</TableCell>
                <TableCell>{x.registerNumber ?? '—'}</TableCell>
                <TableCell align='right'>
                  <Tooltip title='Mandanten öffnen'>
                    <span>
                      <IconButton
                        size='small'
                        onClick={() =>
                          x.idLegalClient != null &&
                          window.open(`/clients/${x.idLegalClient}`, '_blank')
                        }
                        disabled={x.idLegalClient == null}
                      >
                        <LinkIcon fontSize='small' />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title='Entfernen'>
                    <IconButton
                      size='small'
                      onClick={() =>
                        x.idLegalClient != null &&
                        handleRemove(x.idRelationship)
                      }
                    >
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title='Registerdokumente herunterladen'>
                    <IconButton
                      size='small'
                      onClick={() => {
                        setSelectedClientId(x.idLegalClient);
                        setOpenDialog(true);
                      }}
                    >
                      <PolicyIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {openAssign && (
        <AssignLegalClientsDialog
          onlyJuristic
          assignedLegalClients={takenLegalClients}
          setAssignedLegalClients={handleAssignClients}
          unmount={() => setOpenAssign(false)}
        />
      )}
    </>
  );
};
